/**
 * Der Ton läuft durch **eine** Tonkette, nicht durch ein Element je Clip.
 *
 * Warum der Umbau: Safari auf iOS lässt ein `<audio>`-Element nur klingen,
 * wenn sein allererstes `play()` aus einer Berührung kam -- und jedes Element
 * einzeln. Wir haben das versucht (7.37, 7.37a–c), und es ging jedes Mal an
 * einer anderen Ecke schief: ein Anlauf, der einen anderen abbricht; eine
 * Absage, die nie kommt; ein Riegel, der zuklemmt. Sascha, am 6. September:
 * „Canva wird das doch auch hinbekommen. Die sind auch webbasiert. Oder
 * Spiele.“ Stimmt — und die machen es anders.
 *
 * Der übliche Weg ist ein einziger `AudioContext`. iOS erlaubt **ihn**, nicht
 * jede Quelle darin; er wacht beim ersten Tippen auf und bleibt danach wach.
 * Was gespielt wird, sind `AudioBufferSourceNode`s: die fängt man mit
 * `start()` an, das ist keine Bitte, sondern ein Befehl -- kein Versprechen,
 * keine Absage, nichts, was hängen kann. Und die Lautstärke macht ein
 * `GainNode`, was auf dem iPhone ohnehin der einzige Weg ist: `element.volume`
 * ignoriert iOS seit jeher.
 *
 * Der Preis ist das Vorbereiten: eine Datei muss einmal geladen und dekodiert
 * werden, bevor sie so klingen kann. Das passiert im Hintergrund, sobald der
 * Schnitt den Clip kennt. Geht es nicht -- zu lang, falsches Format, kein
 * Speicher --, sagt die Kette das, und die Vorschau nimmt für diesen Clip
 * wieder ein Element. Schlechter als vorher wird es dadurch nie.
 */

import { tonAnfang } from '@schnitt/shared';

type Zustand = 'aus' | 'wach' | 'schläft' | 'zu';

let kontext: AudioContext | null = null;
/** Was schon dekodiert ist -- oder gerade wird, oder nicht geht. */
const speicher = new Map<string, AudioBuffer | 'laedt' | 'geht-nicht'>();
/** Was gerade klingt: je Clip eine Quelle mit ihrem Regler. */
const laufend = new Map<string, { quelle: AudioBufferSourceNode; regler: GainNode }>();
/**
 * Wo eine Quelle von selbst zu Ende war. Ohne das fängt der nächste Durchlauf
 * dieselbe Stelle wieder an -- Saschas „VoiceOvers wiederholen sich zum teil
 * am Ende“ (7.39).
 */
const durch = new Map<string, number>();

/**
 * **Der Abgriff für den Pegelmesser** (Sascha, 28. Oktober, mit einem Bild
 * eines L/R-Messers: „bitte sowas bauen“).
 *
 * Alle Regler der Kette laufen in *einen* Eingang; von dort geht es über den
 * Lautsprecher-Regler hinaus und, daneben, durch einen Kanaltrenner in zwei
 * Analyser. Gemessen wird also, was die Kette wirklich spielt -- in Stereo,
 * wie die Datei es hat, auf jedem Gerät, auch am iPhone. Der
 * Lautsprecher-Regler liegt *hinter* dem Abgriff: wer den Ton ausschaltet,
 * sieht den Pegel weiter.
 *
 * Nicht gemessen wird, was ausnahmsweise über ein Element läuft (zu lang,
 * Format geht nicht, siehe oben) -- dessen Ton kommt an der Kette vorbei.
 */
interface Abgriff {
  kontext: AudioContext;
  eingang: GainNode;
  lautsprecher: GainNode;
  links: AnalyserNode;
  rechts: AnalyserNode;
  proben: Float32Array<ArrayBuffer>;
}
let abgriff: Abgriff | null = null;
/** Ob der Lautsprecher still ist (der Knopf „Ton aus“). */
let leise = false;

function ausgang(k: AudioContext): AudioNode {
  if (abgriff?.kontext === k) return abgriff.eingang;
  const eingang = k.createGain();
  const lautsprecher = k.createGain();
  lautsprecher.gain.value = leise ? 0 : 1;
  eingang.connect(lautsprecher);
  lautsprecher.connect(k.destination);
  // Zwei Ausgänge: eine Mono-Datei kommt so auf beiden gleich an.
  const teiler = k.createChannelSplitter(2);
  eingang.connect(teiler);
  const links = k.createAnalyser();
  const rechts = k.createAnalyser();
  // 2048 Proben sind bei 48 kHz gut 40 ms -- länger als ein Bild, also geht
  // zwischen zwei Messungen keine Spitze verloren.
  links.fftSize = rechts.fftSize = 2048;
  teiler.connect(links, 0);
  teiler.connect(rechts, 1);
  abgriff = { kontext: k, eingang, lautsprecher, links, rechts, proben: new Float32Array(2048) };
  return eingang;
}

/** Den Lautsprecher still stellen oder wieder an. Der Pegel misst weiter. */
export function lautsprecher(an: boolean): void {
  leise = !an;
  if (abgriff) abgriff.lautsprecher.gain.value = an ? 1 : 0;
}

/**
 * Die Spitze je Kanal seit der letzten Frage, 0…1 (1 ist 0 dBFS). `null`,
 * solange die Kette nie gespielt hat.
 */
export function pegel(): [number, number] | null {
  if (!abgriff || abgriff.kontext.state !== 'running') return null;
  const spitze = (a: AnalyserNode): number => {
    const p = abgriff!.proben;
    a.getFloatTimeDomainData(p);
    let max = 0;
    for (let i = 0; i < p.length; i++) {
      const v = Math.abs(p[i]!);
      if (v > max) max = v;
    }
    return max;
  };
  return [spitze(abgriff.links), spitze(abgriff.rechts)];
}

/**
 * Ein Prüfton durch dieselbe Kette (für den Durchgang): 1 kHz, links und
 * rechts mit eigener Spitze, so lange wie angegeben. Was der Messer daraus
 * macht, ist genau das, was er aus einem Clip machen würde.
 */
export function pruefton(links: number, rechts: number, sekunden: number): boolean {
  const k = bauen();
  if (!k) return false;
  if (k.state !== 'running') void k.resume().catch(() => undefined);
  const laenge = Math.max(1, Math.round(k.sampleRate * sekunden));
  const puffer = k.createBuffer(2, laenge, k.sampleRate);
  const l = puffer.getChannelData(0);
  const r = puffer.getChannelData(1);
  for (let i = 0; i < laenge; i++) {
    const s = Math.sin((2 * Math.PI * 1000 * i) / k.sampleRate);
    l[i] = s * links;
    r[i] = s * rechts;
  }
  const quelle = k.createBufferSource();
  quelle.buffer = puffer;
  quelle.connect(ausgang(k));
  quelle.onended = () => quelle.disconnect();
  quelle.start();
  return true;
}

/** Länger als das wird nicht dekodiert: ein Zweistünder im Speicher hilft niemandem. */
const MAX_SEKUNDEN = 15 * 60;

/**
 * Die stille Schleife -- der Grund, warum auf dem iPhone überhaupt etwas zu
 * hören ist.
 *
 * iOS ordnet den Ton einer Seite einer *Kategorie* zu. Läuft nur Web Audio,
 * ist das die leise Kategorie: der kleine Schalter an der Seite des Geräts
 * schaltet sie stumm, und die Lautstärketasten regeln den Klingelton statt
 * der Wiedergabe. Erst ein Medienelement, das **nicht stumm** ist und wirklich
 * spielt, hebt die Seite in die Kategorie „Wiedergabe“ -- danach gilt der
 * Schalter nicht mehr und die Tasten regeln, was man hört.
 *
 * Ein stumm geschaltetes Element tut das ausdrücklich *nicht*. Genau das
 * hatten wir: freigeschaltet wurde mit `muted = true`, und die Vorschau
 * spielte den Ton danach durch die Kette. Am Rechner egal, am iPhone der
 * Unterschied zwischen Ton und keinem Ton (Sascha, 7. September: „Ich kann
 * leider den Ton am Handy nicht hören. Aber am Desktop funktioniert es
 * wunderbar.“).
 *
 * Der Inhalt ist eine Sekunde Stille in Schleife. Man hört sie nicht; sie ist
 * nur da, damit das Betriebssystem die Seite richtig einsortiert.
 */
let sitzung: HTMLAudioElement | null = null;

export function sitzungOffen(): boolean {
  return Boolean(sitzung && !sitzung.paused);
}

function haltDieSitzungOffen(): void {
  if (typeof document === 'undefined') return;
  if (!sitzung) {
    const el = document.createElement('audio');
    el.src = '/stille.wav';
    el.loop = true;
    // Beides mit Absicht und beides nötig: stumm zählt nicht, und `volume`
    // ignoriert iOS ohnehin -- der Inhalt ist ja Stille.
    el.muted = false;
    el.volume = 1;
    el.setAttribute('playsinline', '');
    // Damit das Freischalten der Vorschau dieses Element in Ruhe lässt: es
    // setzt sonst `muted = true` auf allem, was es findet.
    el.dataset.stille = 'ja';
    el.style.display = 'none';
    document.body.appendChild(el);
    sitzung = el;
  }
  if (sitzung.paused) void sitzung.play().catch(() => undefined);
}

function schliessDieSitzung(): void {
  if (!sitzung) return;
  sitzung.pause();
  sitzung.remove();
  sitzung = null;
}

function bauen(): AudioContext | null {
  if (kontext) return kontext;
  const Klasse = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Klasse) return null;
  try {
    kontext = new Klasse();
  } catch {
    return null;
  }
  return kontext;
}

export function zustand(): Zustand {
  if (!kontext) return 'aus';
  if (kontext.state === 'running') return 'wach';
  if (kontext.state === 'closed') return 'zu';
  return 'schläft';
}

/**
 * Aus einer Berührung heraus aufrufen. Das ist der einzige Augenblick, in dem
 * iOS die Kette aufwachen lässt -- danach bleibt sie wach, für alles.
 */
export function wecke(): void {
  // Zuerst die Kategorie, dann die Kette. Beides gehört in dieselbe
  // Berührung -- ein `play()` und ein `resume()` sind aus iOS' Sicht
  // dasselbe Zugeständnis, und es gilt nur jetzt.
  haltDieSitzungOffen();
  const k = bauen();
  if (!k) return;
  if (k.state !== 'running') void k.resume().catch(() => undefined);
}

/**
 * Eine Datei bereitlegen. Läuft im Hintergrund; wer danach fragt, bekommt
 * `null`, solange sie nicht fertig ist, und das ist kein Fehler.
 */
export function lege(id: string, url: string): void {
  if (speicher.has(id)) return;
  const k = bauen();
  if (!k) {
    speicher.set(id, 'geht-nicht');
    return;
  }
  speicher.set(id, 'laedt');
  void (async () => {
    try {
      const antwort = await fetch(url);
      if (!antwort.ok) throw new Error(String(antwort.status));
      const roh = await antwort.arrayBuffer();
      const puffer = await k.decodeAudioData(roh);
      speicher.set(id, puffer.duration > MAX_SEKUNDEN ? 'geht-nicht' : puffer);
    } catch {
      // Kein Grund zur Aufregung: dann nimmt die Vorschau ein Element.
      speicher.set(id, 'geht-nicht');
    }
  })();
}

/** Ob dieser Clip über die Kette laufen kann. */
export function bereit(id: string): boolean {
  return speicher.get(id) instanceof AudioBuffer;
}

/** Ob er es je können wird. `false` heißt: nimm ein Element. */
export function moeglich(id: string): boolean {
  return speicher.get(id) !== 'geht-nicht';
}

/**
 * Diesen Clip ab `versatz` Sekunden klingen lassen. Ist er schon dran,
 * passiert nichts -- außer dass der Regler nachgeführt wird.
 *
 * `id` ist der Clip, `datei` das Stück Ton: zwei Clips aus derselben Aufnahme
 * teilen sich das Dekodierte, klingen aber jeder für sich.
 */
export function spiele(
  id: string,
  datei: string,
  versatz: number,
  lautstaerke: number,
  tempo: number,
  rest = Infinity,
): void {
  const k = kontext;
  const puffer = speicher.get(datei);
  if (!k || !(puffer instanceof AudioBuffer)) return;
  const schon = laufend.get(id);
  if (schon) {
    schon.regler.gain.value = lautstaerke;
    if (Math.abs(schon.quelle.playbackRate.value - tempo) > 0.004) schon.quelle.playbackRate.value = tempo;
    return;
  }
  // Ob überhaupt angefangen wird, steht im gemeinsamen Teil (7.39): eine
  // Stelle, die von selbst zu Ende war, fängt nicht noch einmal an -- sonst
  // hört man die letzten Millisekunden in Schleife.
  const entscheid = tonAnfang({ versatz, laenge: puffer.duration, rest, schonDurch: durch.get(id) ?? null });
  if (!entscheid.anfangen) return;
  const regler = k.createGain();
  regler.gain.value = lautstaerke;
  regler.connect(ausgang(k));
  const quelle = k.createBufferSource();
  quelle.buffer = puffer;
  quelle.playbackRate.value = tempo;
  quelle.connect(regler);
  quelle.onended = () => {
    if (laufend.get(id)?.quelle !== quelle) return;
    laufend.delete(id);
    // Bis hierhin ist diese Quelle gekommen. Von hier aus nicht noch einmal.
    durch.set(id, entscheid.ab + entscheid.dauer);
  };
  laufend.set(id, { quelle, regler });
  // `start` ist keine Bitte: es kann nicht abgelehnt werden und nichts
  // abbrechen. Genau deshalb der ganze Umbau. Die Länge gehört dazu: eine
  // Quelle, die von selbst aufhört, wartet nicht darauf, dass jemand
  // rechtzeitig `stoppe` ruft.
  quelle.start(0, entscheid.ab, entscheid.dauer);
}

/** Diesen Clip anhalten. Eine Quelle ist Einweg -- der nächste Start baut eine neue. */
export function stoppe(id: string): void {
  // Wer angehalten wird, fängt beim nächsten Mal wieder von vorn an dürfen.
  durch.delete(id);
  const da = laufend.get(id);
  if (!da) return;
  laufend.delete(id);
  try {
    da.quelle.onended = null;
    da.quelle.stop();
  } catch {
    // Schon vorbei. Auch gut.
  }
  da.quelle.disconnect();
  da.regler.disconnect();
}

/**
 * Vergessen, dass dieser Clip schon einmal durch war -- der nächste Anlauf
 * fängt wieder an. Beim Anhalten der Wiedergabe für alle: wer noch einmal
 * abspielt, will ihn noch einmal hören, auch wenn er kürzer als eine
 * Vierteleskunde ist.
 */
export function vergiss(id: string): void {
  durch.delete(id);
}

export function vergissAlles(): void {
  durch.clear();
}

/** Alles anhalten -- beim Verlassen des Schnittplatzes. */
export function stoppeAlles(): void {
  for (const id of [...laufend.keys()]) stoppe(id);
  durch.clear();
  schliessDieSitzung();
}

/**
 * Wie es dem Ton geht, in Zahlen. Für die Diagnose auf dem Gerät -- beim
 * nächsten Mal soll nicht wieder geraten werden müssen, warum nichts zu hören
 * ist: entweder schläft die Kette, oder die Sitzung ist zu, oder eine Datei
 * liess sich nicht dekodieren. Drei verschiedene Ursachen, drei verschiedene
 * Handgriffe.
 */
export function bericht(): { kette: Zustand; sitzung: boolean; bereit: number; laedt: number; gehtNicht: number; klingt: number } {
  let bereitZahl = 0;
  let laedtZahl = 0;
  let gehtNichtZahl = 0;
  for (const wert of speicher.values()) {
    if (wert instanceof AudioBuffer) bereitZahl += 1;
    else if (wert === 'laedt') laedtZahl += 1;
    else gehtNichtZahl += 1;
  }
  return {
    kette: zustand(),
    sitzung: sitzungOffen(),
    bereit: bereitZahl,
    laedt: laedtZahl,
    gehtNicht: gehtNichtZahl,
    klingt: laufend.size,
  };
}

/** Welche Clips gerade über die Kette klingen. Für die Prüfung und die Diagnose. */
export function klingt(): string[] {
  return [...laufend.keys()];
}
